'use client';

import React, { useEffect } from 'react';

import { track } from '@repo/analytics';
import { Button } from '@repo/ui/components/ui/button';

import { clientLogger } from '@/lib/client-logger';

/**
 * Route-group error boundary for the (app) surface — the root feed `/`,
 * /youtube, /articles, brand and channel pages. Restores the crash
 * telemetry that lived in the deleted /home error boundary (removed in
 * 93210471 when the combined feed moved to `/`): uncaught render errors
 * on the flagship routes fire the registered `home_feed_error_captured`
 * event and log via clientLogger instead of falling through to the
 * app-wide global-error with no typed signal.
 *
 * The reset() callback is provided by Next.js — it re-mounts the route
 * subtree, which is the right action for transient render-time issues.
 */
export default function AppRouteError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    track('home_feed_error_captured', {
      boundary_level: 'route',
      error_message: error.message,
      digest: error.digest,
    });
    clientLogger.error('[home-v2-feed] route error', {
      message: error.message,
      digest: error.digest,
    });
  }, [error]);

  return (
    <div className="flex w-full flex-col items-center px-4 py-12">
      <div
        role="alert"
        className="flex w-full max-w-md flex-col items-center gap-3 rounded-md border border-rubric-red-muted bg-surface-base p-6 text-center"
      >
        <h2 className="font-satoshi text-[16px] font-bold text-ink-primary">
          Something went wrong loading this page.
        </h2>
        <p className="font-inter text-[13px] text-ink-tertiary">
          The team has been notified. Try again, this is usually a temporary
          issue.
        </p>
        <Button
          variant="default"
          onClick={() => reset()}
          className="bg-accent-electric text-white hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-electric focus-visible:ring-offset-2"
        >
          Reload
        </Button>
      </div>
    </div>
  );
}
